'use client';

import { CardCarousel as SharedCardCarousel } from 'components/shared/Card/CardCarousel';

import IconVariant from './variants/IconVariant';
import ImageVariant from './variants/ImageVariant';

import { getColorwayName } from 'utils/formatters';
import { cn } from 'utils/styles';

import { liveUpdateAttrs } from 'libs/contentful/utils';

import { CardCarouselProps, CardCarouselClassNames, CardVariant, CardVariantProps, IconVariantProps } from './types';

const CardCarousel = ({
  sys,
  sectionName,
  color,
  margins,
  cardStyle,
  cards,
  className,
  paginationPortalRef,
}: CardCarouselProps) => {
  const internalClassNames: CardCarouselClassNames = {
    wrapper: cn(className?.wrapper),
    iconVariant: {
      icon: cn(className?.iconVariant?.icon),
      cardWrapper: {
        wrapper: cn('max-h-[50rem]', className?.iconVariant?.cardWrapper?.wrapper),
        header: cn(className?.iconVariant?.cardWrapper?.header),
        content: cn(className?.iconVariant?.cardWrapper?.content),
      },
      cardContent: {
        wrapper: cn(className?.iconVariant?.cardContent?.wrapper),
        header: {
          wrapper: cn(className?.iconVariant?.cardContent?.header?.wrapper),
          eyebrow: cn(className?.iconVariant?.cardContent?.header?.eyebrow),
          headline: cn(className?.iconVariant?.cardContent?.header?.headline),
        },
        copy: cn(className?.iconVariant?.cardContent?.copy),
      },
      hyperlinkedImages: cn(className?.iconVariant?.hyperlinkedImages),
      eyebrow: cn(className?.iconVariant?.eyebrow),
      cta: cn(className?.iconVariant?.cta),
    },
    imageVariant: {
      cardWrapper: {
        wrapper: cn('max-h-[50rem]', className?.imageVariant?.cardWrapper?.wrapper),
        header: cn(className?.imageVariant?.cardWrapper?.header),
        content: cn(className?.imageVariant?.cardWrapper?.content),
      },
      cardContent: {
        wrapper: cn(className?.imageVariant?.cardContent?.wrapper),
        header: {
          wrapper: cn(className?.imageVariant?.cardContent?.header?.wrapper),
          eyebrow: cn(className?.imageVariant?.cardContent?.header?.eyebrow),
          headline: cn(className?.imageVariant?.cardContent?.header?.headline),
        },
        copy: cn(className?.imageVariant?.cardContent?.copy),
      },
      media: {
        wrapper: cn(className?.imageVariant?.media?.content),
        content: cn(className?.imageVariant?.media?.wrapper),
      },
    },
  };

  const getCardVariant = (variant: CardVariant, props: CardVariantProps) => {
    const bakedProps = { ...props, cardStyle };

    if (variant === CardVariant.Icon) {
      return (
        <IconVariant
          {...bakedProps}
          key={props.sys?.id}
          cta={(props as IconVariantProps).cta}
          className={internalClassNames.iconVariant}
        />
      );
    }

    return <ImageVariant {...bakedProps} key={props.sys?.id} className={internalClassNames.imageVariant} />;
  };

  return (
    <section
      data-testid="section"
      {...(sectionName != null && { id: encodeURIComponent(sectionName) })}
      {...liveUpdateAttrs(sys?.id, 'cardCarousel')}>
      <SharedCardCarousel
        totalItems={cards.items.length}
        color={getColorwayName(color)}
        margins={margins}
        sectionName={sectionName}
        paginationPortalRef={paginationPortalRef}>
        {cards.items.map((card, index) => {
          return getCardVariant(card.variant, {
            ...card,
            index,
            total: cards.items.length,
          });
        })}
      </SharedCardCarousel>
    </section>
  );
};

export default CardCarousel;
